import { z } from 'zod';
import { ExceptionRate, PerformedBy, RaciLetter, StepType, SystemUsage } from './enums.js';
/**
 * The process graph — the one schema everything derives from (docs/BUILD.md §10).
 *
 * Consumers:
 *   1. Claude structured output (api): `zodOutputFormat(ProcessGraphShape)`
 *   2. API request validation:        `ProcessGraphSchema.parse(body)`
 *   3. UI editor types:                `ProcessGraph`, `Step`, `Transition`
 *
 * Rules that keep it compatible with Claude structured outputs:
 *   - No `.optional()`. Every field is required; absence is expressed with `.nullable()`.
 *   - No recursion.
 *   - `.min()/.max()/.regex()` are fine: the SDK strips them from the wire schema and
 *     validates client-side. Do not rely on them to constrain generation.
 *   - `.describe()` text is sent to the model as the JSON-schema description. It is the
 *     field-level prompt. Write it for the model, not for developers.
 *
 * Identity: steps are keyed by a snake_case `key` that is stable within a draft. The API
 * maps keys to `step.logical_id` (uuid) when persisting — the model never sees uuids.
 * Roles and systems are names as the owner spoke them; the API resolves them against
 * `org_role` and `business_system`.
 */
export const STEP_KEY_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;
export const StepKey = z
    .string()
    .regex(STEP_KEY_PATTERN, 'Step key must be snake_case, start with a letter, 2–64 chars')
    .describe('Short snake_case identifier unique within this process, e.g. "check_credit_terms".');
export const StepSystemSchema = z.object({
    name: z
        .string()
        .min(1)
        .max(120)
        .describe('The system as the owner named it, e.g. "NetSuite", "HubSpot", "a shared spreadsheet".'),
    usage: SystemUsage.describe('Whether this step reads from, writes to, or both.'),
});
export const RaciEntrySchema = z.object({
    role: z.string().min(2).max(120).describe('Job title.'),
    letter: RaciLetter.describe('R responsible, A accountable, C consulted, I informed.'),
});
/**
 * Raw signals the interviewer can observe. The automation score is computed from these
 * in code (api/domain/scoring) — the model reports evidence, it does not grade.
 */
export const AutomationSignalsSchema = z.object({
    ruleClarity: z
        .number()
        .int()
        .min(1)
        .max(5)
        .describe('1 = every case is handled differently; 5 = a written rule covers every case.'),
    dataAvailability: z
        .number()
        .int()
        .min(1)
        .max(5)
        .describe("1 = the inputs live in someone's head or on paper; 5 = every input is in a system an agent can read."),
    exceptionRate: ExceptionRate.describe('How often the normal path is abandoned.'),
    volumePerMonth: z
        .number()
        .int()
        .min(0)
        .nullable()
        .describe('Roughly how many times this step runs per month, or null if the owner does not know.'),
});
export const StepSchema = z.object({
    key: StepKey,
    name: z
        .string()
        .min(3)
        .max(200)
        .describe('Short verb phrase: "Check credit terms", not "Credit". For decisions, a question: "Margin above floor?"'),
    description: z
        .string()
        .max(2000)
        .nullable()
        .describe('What actually happens, as a newcomer would need it explained. Null if the name is enough.'),
    role: z
        .string()
        .min(2)
        .max(120)
        .describe('The job title that performs this step. Never a person\'s name. "Account Executive", not "Sarah".'),
    performedBy: PerformedBy,
    stepType: StepType,
    systems: z.array(StepSystemSchema).max(10).describe('Systems touched. Empty array if none.'),
    touchMinutes: z
        .number()
        .int()
        .min(0)
        .max(100_000)
        .nullable()
        .describe('Minutes of actual work in this step. Null if unknown. Zero for start, end, and wait steps.'),
    waitMinutes: z
        .number()
        .int()
        .min(0)
        .max(1_000_000)
        .nullable()
        .describe('Minutes typically spent waiting before the next step can begin. Null if unknown.'),
    notes: z.string().max(2000).nullable().describe('Exceptions, tips, history. Null if none.'),
    raci: z
        .array(RaciEntrySchema)
        .max(20)
        .describe('Include at least the Responsible role (usually the step role) and exactly one Accountable role.'),
    requiresJudgment: z
        .boolean()
        .describe('True if the person weighs factors that no written rule fully covers.'),
    involvesNegotiation: z
        .boolean()
        .describe('True if the step involves agreeing terms, price, scope, or timing with another party.'),
    affectsClientTrust: z
        .boolean()
        .describe('True if a client would notice, and care, whether a person or a machine did this.'),
    automationSignals: AutomationSignalsSchema.nullable().describe('Evidence for automation potential. Null for start, end, wait, and any step where the owner gave no information.'),
    whyNotAutomate: z
        .string()
        .max(1000)
        .nullable()
        .describe("The owner's stated reason a person must do this, in their words. Null if not discussed."),
    overrideReason: z
        .string()
        .max(1000)
        .nullable()
        .describe('Always null at capture. Set only by the owner in review when choosing a performer outside the default for a flagged step.'),
});
export const TransitionSchema = z.object({
    from: StepKey,
    to: StepKey,
    condition: z
        .string()
        .max(200)
        .nullable()
        .describe('Edge label. Required (non-null) on every edge leaving a decision, e.g. "Approved", "Over $50k". Null on plain sequence.'),
    isDefault: z
        .boolean()
        .describe('The path taken most often. Exactly one outgoing edge of each decision is the default. False on plain sequence.'),
});
/**
 * Structural shape without cross-field refinements. Pass THIS to `zodOutputFormat` —
 * refinements are not representable in JSON schema and some converters reject the
 * ZodEffects wrapper. Validate the parsed result with `ProcessGraphSchema`.
 */
export const ProcessGraphShape = z.object({
    name: z.string().min(3).max(200).describe('Process name as the department would say it: "Quote to Order".'),
    purpose: z
        .string()
        .max(1000)
        .nullable()
        .describe('One or two sentences: what this process exists to produce, and for whom.'),
    steps: z.array(StepSchema).min(2).max(60),
    transitions: z
        .array(TransitionSchema)
        .max(200)
        .describe('Every edge in the flow. Rework loops are legal and expected.'),
    openQuestions: z
        .array(z.string().max(500))
        .max(30)
        .describe('Things the owner could not answer or that need another person. Empty array if none.'),
});
/** Structural shape plus referential integrity. Use for all validation. */
export const ProcessGraphSchema = ProcessGraphShape.superRefine((g, ctx) => {
    const keys = new Set();
    g.steps.forEach((s, i) => {
        if (keys.has(s.key)) {
            ctx.addIssue({ code: 'custom', path: ['steps', i, 'key'], message: `Duplicate step key "${s.key}"` });
        }
        keys.add(s.key);
    });
    g.transitions.forEach((t, i) => {
        if (!keys.has(t.from)) {
            ctx.addIssue({ code: 'custom', path: ['transitions', i, 'from'], message: `Unknown step key "${t.from}"` });
        }
        if (!keys.has(t.to)) {
            ctx.addIssue({ code: 'custom', path: ['transitions', i, 'to'], message: `Unknown step key "${t.to}"` });
        }
    });
});
//# sourceMappingURL=process-graph.js.map